import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Network, RefreshCw, Loader2 } from 'lucide-react';
import { formatDate } from '../utils/dateFormatter';
import { FrameworkLine, StatusBadge } from './directoryWidgetCommon';
import FindingCrossLinks from './FindingCrossLinks';

const SuspendedUsersWidget = ({ workspaceId, gwConnected }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    try {
      setLoading(true);
      const resp = await axios.get('/api/employees/suspended-user-review', {
        params: { workspace_id: workspaceId },
      });
      setData(resp.data);
    } catch (err) {
      console.error('suspended-user-review load failed', err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  if (!gwConnected) return null;

  const t = data?.totals || {};

  return (
    <Card className="border-slate-200 mb-6" data-testid="suspended-users-widget">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Network className="w-5 h-5 text-slate-500" strokeWidth={1.7} />
              Suspended User Deprovisioning
              {data && <StatusBadge status={data.status} />}
            </CardTitle>
            <FrameworkLine>
              SOC 2 CC6.2/CC6.3 · ISO 27001 A.5.18/A.6.5 · HIPAA §164.308(a)(3)
            </FrameworkLine>
          </div>
          <Button onClick={load} variant="ghost" size="sm" disabled={loading}
                  data-testid="suspended-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {loading && !data && (
          <div className="py-6 text-center text-sm text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
            Evaluating directory…
          </div>
        )}
        {data && (
          <>
            <p className="text-sm text-slate-700 mb-3" data-testid="suspended-proof">{data.proof}</p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
              <div className="border rounded-lg p-3 bg-slate-50 border-slate-200 text-slate-700">
                <div className="text-2xl font-semibold leading-none">{t.suspended_total}</div>
                <div className="text-xs font-medium mt-1">Suspended accounts</div>
              </div>
              <div className={`border rounded-lg p-3 ${t.suspended_with_admin_role > 0 ? 'bg-red-50 border-red-200 text-red-900' : 'bg-emerald-50 border-emerald-200 text-emerald-900'}`}>
                <div className="text-2xl font-semibold leading-none">{t.suspended_with_admin_role}</div>
                <div className="text-xs font-medium mt-1">Still hold admin role — critical</div>
              </div>
              <div className={`border rounded-lg p-3 ${t.suspended_with_active_groups > 0 ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-emerald-50 border-emerald-200 text-emerald-900'}`}>
                <div className="text-2xl font-semibold leading-none">{t.suspended_with_active_groups}</div>
                <div className="text-xs font-medium mt-1">Retain group memberships</div>
              </div>
              <div className="border rounded-lg p-3 bg-emerald-50 border-emerald-200 text-emerald-900">
                <div className="text-2xl font-semibold leading-none">{t.properly_deprovisioned}</div>
                <div className="text-xs font-medium mt-1">Properly deprovisioned</div>
              </div>
            </div>

            {(data.critical_users || []).length > 0 && (
              <div className="border rounded-lg border-slate-200 mb-3" data-testid="suspended-critical-list">
                <div className="px-3 py-2 border-b border-slate-100 text-xs font-medium text-slate-700">
                  Affected accounts ({data.critical_users.length})
                </div>
                <div className="divide-y divide-slate-100 bg-white rounded-b-lg">
                  {data.critical_users.map((u) => (
                    <div key={u.source_user_id || u.primary_email}
                         className="px-3 py-2 text-sm flex items-start gap-2"
                         data-testid={`suspended-affected-${u.source_user_id || u.primary_email}`}>
                      <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${u.severity === 'critical' ? 'bg-red-500' : 'bg-amber-500'}`} />
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900 truncate">{u.full_name || u.primary_email}</div>
                        {u.full_name && <div className="text-xs text-slate-500 truncate">{u.primary_email}</div>}
                        <div className={`text-xs ${u.severity === 'critical' ? 'text-red-700' : 'text-amber-700'}`}>{u.issue}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.what_would_fix && (
              <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md p-3">
                <span className="font-medium text-slate-700">Next steps:</span> {data.what_would_fix}
              </div>
            )}

            <FindingCrossLinks
              ruleKey="directory.access.suspended_user_review"
              matches={[]}
              workspaceId={workspaceId}
            />

            <div className="mt-3 text-[11px] text-slate-400">
              Evaluated {formatDate(data.evaluated_at)}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default SuspendedUsersWidget;
